import {
  subscribeUser,
  listUserRows,
  insertRow,
  updateRow,
  deleteRow,
  newId,
  handleDbError,
} from '../lib/db';
import {
  AccountingAccount,
  AccountingCategory,
  JournalEntry,
  Account,
  PlannedConcept,
  ManagedDocument,
  Transaction
} from '../types';
import { getInstitutionById } from '../constants/institutions';
import { calculateTransactionBalances } from '../utils/transactionBalance';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export const subscribeToAccountingAccounts = (userId: string, callback: (accounts: AccountingAccount[]) => void) => {
  return subscribeUser<AccountingAccount>(
    'accountingAccounts',
    userId,
    callback,
    { orderBy: 'code', ascending: true },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/accountingAccounts`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const subscribeToAccountingEntries = (userId: string, callback: (entries: JournalEntry[]) => void) => {
  // El query original ordenaba por date desc + createdAt desc; el segundo criterio se aplica en JS.
  return subscribeUser<JournalEntry>(
    'accountingEntries',
    userId,
    (rows) => {
      const sorted = [...rows].sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? 1 : -1;
        const aCreated = a.createdAt || '';
        const bCreated = b.createdAt || '';
        return aCreated < bCreated ? 1 : aCreated > bCreated ? -1 : 0;
      });
      callback(sorted);
    },
    { orderBy: 'date', ascending: false },
    (error) => {
      try {
        handleDbError(error, OperationType.GET, `users/${userId}/accountingEntries`);
      } catch {
        /* logueado; la suscripción sigue viva */
      }
    },
  );
};

export const createAccountingAccount = async (userId: string, data: Omit<AccountingAccount, 'id' | 'createdAt' | 'userId'>) => {
  return insertRow<AccountingAccount>('accountingAccounts', {
    ...data,
    userId,
    createdAt: new Date().toISOString()
  });
};

export const updateAccountingAccount = async (userId: string, id: string, data: Partial<AccountingAccount>) => {
  return updateRow('accountingAccounts', id, data);
};

export const deleteAccountingAccount = async (userId: string, id: string) => {
  return deleteRow('accountingAccounts', id);
};

export const createJournalEntry = async (userId: string, data: Omit<JournalEntry, 'id' | 'createdAt' | 'userId'>) => {
  return insertRow<JournalEntry>('accountingEntries', {
    ...data,
    userId,
    createdAt: new Date().toISOString()
  });
};

export const updateJournalEntry = async (userId: string, id: string, data: Partial<JournalEntry>) => {
  return updateRow('accountingEntries', id, data);
};

export const deleteJournalEntry = async (userId: string, id: string) => {
  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (primero se desvinculan los documentos; si algo falla a mitad, el asiento
  // sigue existiendo y ningún documento queda apuntando a un asiento borrado).

  // 1. Quitar referencia de los documentos vinculados
  const allDocs = await listUserRows<ManagedDocument>('managedDocuments', userId);
  const linkedDocs = allDocs.filter(d => d.journalEntryId === id);

  for (const d of linkedDocs) {
    await updateRow('managedDocuments', d.id, { journalEntryId: null });
  }

  // 2. Eliminar el asiento
  await deleteRow('accountingEntries', id);
};

/**
 * Repairs missing documentLocatorCode fields in JournalEntry documents
 * by checking their linked ManagedDocuments.
 */
export const repairReferenceCodes = async (userId: string) => {
  const [entries, docs] = await Promise.all([
    listUserRows<JournalEntry>('accountingEntries', userId),
    listUserRows<ManagedDocument>('managedDocuments', userId)
  ]);

  // nota: sin batch atómico; updates independientes entre sí, un fallo parcial
  // deja asientos ya reparados y el resto se repara en una nueva corrida.
  let repairCount = 0;

  for (const entry of entries) {
    if (entry.documentLocatorCode) continue;

    // Find the managed document that points to this journal entry
    const linkedDoc = docs.find(d => d.journalEntryId === entry.id);

    if (linkedDoc?.locatorCode) {
      await updateRow('accountingEntries', entry.id, { documentLocatorCode: linkedDoc.locatorCode });
      repairCount++;
    }
  }

  return repairCount;
};

export const groupJournalLines = (lines: any[]): any[] => {
  const grouped: Record<string, { accountId: string, debit: number, credit: number, descriptions: string[] }> = {};

  lines.forEach(line => {
    if (!line.accountId) return;

    if (!grouped[line.accountId]) {
      grouped[line.accountId] = {
        accountId: line.accountId,
        debit: 0,
        credit: 0,
        descriptions: []
      };
    }

    const item = grouped[line.accountId];
    item.debit += parseFloat(line.debit?.toString() || '0');
    item.credit += parseFloat(line.credit?.toString() || '0');
    if (line.description && !item.descriptions.includes(line.description)) {
      item.descriptions.push(line.description);
    }
  });

  return Object.values(grouped)
    .filter(item => item.debit !== 0 || item.credit !== 0)
    .map(item => ({
      accountId: item.accountId,
      debit: item.debit,
      credit: item.credit,
      description: item.descriptions.join(', ')
    }));
};

export const resetAccountingData = async (userId: string) => {
  // We need to fetch them first as batch delete requires references
  // Since we can't easily fetch in the service without a separate call,
  // we'll handle this in the component or provide a clear helper.
  // For simplicity and safety in this environment, I'll provide the logic to the component.
};

export const generateInitialBalancesEntry = async (userId: string, accounts: AccountingAccount[], financeAccounts: Account[]) => {
  // Find accounts
  const capitalAccount = accounts.find(a => a.code === '3.1' || (a.isSelectable && a.name.toLowerCase().includes('capital social')));
  if (!capitalAccount) {
    throw new Error('No se encontró la cuenta de Capital Social (3.1) para balancear los bancos.');
  }

  const contrapartidaTC = accounts.find(a => a.code === '2.1.04' || a.name.includes('Contrapartida Saldos Tarjetas'));
  if (!contrapartidaTC) {
    throw new Error('No se encontró la cuenta especial de Contrapartida para Tarjetas de Crédito (2.1.04). Por favor reinicializa el módulo.');
  }

  const resultadosAcumulados = accounts.find(a => a.code === '3.3' || (a.isSelectable && a.name.toLowerCase().includes('resultados acumulados')));
  if (!resultadosAcumulados) {
    throw new Error('No se encontró la cuenta de Resultados Acumulados (3.3). Reinicializa el módulo primero.');
  }

  const lines: any[] = [];

  let totalDebit = 0;
  let totalCredit = 0;

  let totalCapitalSocial = 0;
  let totalCardsCreditLimit = 0;

  for (const acc of accounts) {
    if (acc.linkedFinanceAccountId) {
      const financeAcc = financeAccounts.find(fa => fa.id === acc.linkedFinanceAccountId);
      if (financeAcc) {
        if (acc.category === 'ACTIVO') {
          const isCard = acc.code.startsWith('1.1.02') || financeAcc.type === 'TARJETA DE CREDITO';

          if (financeAcc.initialBalance !== 0) {
            if (financeAcc.initialBalance > 0) {
              lines.push({ accountId: acc.id, debit: financeAcc.initialBalance, credit: 0 });
              totalDebit += financeAcc.initialBalance;
            } else {
              lines.push({ accountId: acc.id, debit: 0, credit: Math.abs(financeAcc.initialBalance) });
              totalCredit += Math.abs(financeAcc.initialBalance);
            }
          }

          if (isCard) {
            if (financeAcc.creditLimit && financeAcc.creditLimit > 0) {
              totalCardsCreditLimit += financeAcc.creditLimit;
            }
          } else {
            totalCapitalSocial += financeAcc.initialBalance;
          }
        }
      }
    }
  }

  if (totalCardsCreditLimit > 0) {
    lines.push({ accountId: contrapartidaTC.id, debit: 0, credit: totalCardsCreditLimit });
    totalCredit += totalCardsCreditLimit;
  }

  if (totalCapitalSocial > 0) {
    lines.push({ accountId: capitalAccount.id, debit: 0, credit: totalCapitalSocial });
    totalCredit += totalCapitalSocial;
  } else if (totalCapitalSocial < 0) {
    lines.push({ accountId: capitalAccount.id, debit: Math.abs(totalCapitalSocial), credit: 0 });
    totalDebit += Math.abs(totalCapitalSocial);
  }

  // Cuadrar el asiento usando Resultados Acumulados
  const diff = totalDebit - totalCredit;
  if (diff > 0) {
    lines.push({ accountId: resultadosAcumulados.id, debit: 0, credit: diff });
  } else if (diff < 0) {
    lines.push({ accountId: resultadosAcumulados.id, debit: Math.abs(diff), credit: 0 });
  }

  const validLines = lines.filter(l => l.debit !== 0 || l.credit !== 0);

  if (validLines.length === 0) {
    throw new Error('No hay saldos configurados en las cuentas financieras vinculadas (todo está en 0) o no hay vínculos.');
  }

  return createJournalEntry(userId, {
    date: new Date().toISOString().split('T')[0],
    description: 'Asiento de Apertura - Saldos Iniciales Automáticos',
    type: 'APERTURA',
    lines: validLines
  });
};

export const seedChartOfAccounts = async (userId: string, financeAccounts: Account[], concepts: PlannedConcept[]) => {
  const timestamp = new Date().toISOString();

  type TemplateItem = {
    tempId: string;
    code: string;
    name: string;
    category: AccountingCategory;
    parentTempId: string | null;
    isSelectable: boolean;
    level: number;
    initialBalance: number;
    linkedFinanceAccountId?: string;
    linkedPlannedConceptId?: string;
  };

  const template: TemplateItem[] = [
    // 1 - ACTIVO
    { tempId: '1', code: '1', name: 'ACTIVO', category: 'ACTIVO', parentTempId: null, isSelectable: false, level: 1, initialBalance: 0 },

    // 1.1 ACTIVO CORRIENTE
    { tempId: '1.1', code: '1.1', name: 'ACTIVO CORRIENTE', category: 'ACTIVO', parentTempId: '1', isSelectable: false, level: 2, initialBalance: 0 },
    { tempId: '1.1.01', code: '1.1.01', name: 'Disponibilidades', category: 'ACTIVO', parentTempId: '1.1', isSelectable: false, level: 3, initialBalance: 0 },
    { tempId: '1.1.02', code: '1.1.02', name: 'Tarjetas de Crédito', category: 'ACTIVO', parentTempId: '1.1', isSelectable: false, level: 3, initialBalance: 0 },
    { tempId: '1.1.03', code: '1.1.03', name: 'Cuentas a Cobrar', category: 'ACTIVO', parentTempId: '1.1', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '1.1.04', code: '1.1.04', name: 'Mercaderías / Inventario', category: 'ACTIVO', parentTempId: '1.1', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '1.1.05', code: '1.1.05', name: 'Otros Activos Corrientes', category: 'ACTIVO', parentTempId: '1.1', isSelectable: true, level: 3, initialBalance: 0 },

    // 1.2 ACTIVO NO CORRIENTE
    { tempId: '1.2', code: '1.2', name: 'ACTIVO NO CORRIENTE', category: 'ACTIVO', parentTempId: '1', isSelectable: false, level: 2, initialBalance: 0 },
    { tempId: '1.2.01', code: '1.2.01', name: 'Bienes de Uso / Activo Fijo', category: 'ACTIVO', parentTempId: '1.2', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '1.2.02', code: '1.2.02', name: 'Inversiones a Largo Plazo', category: 'ACTIVO', parentTempId: '1.2', isSelectable: true, level: 3, initialBalance: 0 },

    // 2 - PASIVO
    { tempId: '2', code: '2', name: 'PASIVO', category: 'PASIVO', parentTempId: null, isSelectable: false, level: 1, initialBalance: 0 },

    // 2.1 PASIVO CORRIENTE
    { tempId: '2.1', code: '2.1', name: 'PASIVO CORRIENTE', category: 'PASIVO', parentTempId: '2', isSelectable: false, level: 2, initialBalance: 0 },
    { tempId: '2.1.01', code: '2.1.01', name: 'Cuentas a Pagar Comerciales', category: 'PASIVO', parentTempId: '2.1', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '2.1.02', code: '2.1.02', name: 'Impuestos a Pagar', category: 'PASIVO', parentTempId: '2.1', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '2.1.03', code: '2.1.03', name: 'Sueldos y Jornales a Pagar', category: 'PASIVO', parentTempId: '2.1', isSelectable: true, level: 3, initialBalance: 0 },
    { tempId: '2.1.04', code: '2.1.04', name: 'Contrapartida Saldos Tarjetas', category: 'PASIVO', parentTempId: '2.1', isSelectable: true, level: 3, initialBalance: 0 },

    // 2.2 PASIVO NO CORRIENTE
    { tempId: '2.2', code: '2.2', name: 'PASIVO NO CORRIENTE', category: 'PASIVO', parentTempId: '2', isSelectable: false, level: 2, initialBalance: 0 },
    { tempId: '2.2.01', code: '2.2.01', name: 'Préstamos Bancarios LP', category: 'PASIVO', parentTempId: '2.2', isSelectable: true, level: 3, initialBalance: 0 },

    // 3 - PATRIMONIO
    { tempId: '3', code: '3', name: 'PATRIMONIO', category: 'PATRIMONIO', parentTempId: null, isSelectable: false, level: 1, initialBalance: 0 },
    { tempId: '3.1', code: '3.1', name: 'Capital Social', category: 'PATRIMONIO', parentTempId: '3', isSelectable: true, level: 2, initialBalance: 0 },
    { tempId: '3.2', code: '3.2', name: 'Reservas', category: 'PATRIMONIO', parentTempId: '3', isSelectable: true, level: 2, initialBalance: 0 },
    { tempId: '3.3', code: '3.3', name: 'Resultados Acumulados', category: 'PATRIMONIO', parentTempId: '3', isSelectable: true, level: 2, initialBalance: 0 },
    { tempId: '3.4', code: '3.4', name: 'Resultado del Ejercicio', category: 'PATRIMONIO', parentTempId: '3', isSelectable: true, level: 2, initialBalance: 0 },

    // 4 - INGRESO
    { tempId: '4', code: '4', name: 'INGRESO', category: 'INGRESO', parentTempId: null, isSelectable: false, level: 1, initialBalance: 0 },
    { tempId: '4.1', code: '4.1', name: 'Ingresos Ordinarios', category: 'INGRESO', parentTempId: '4', isSelectable: true, level: 2, initialBalance: 0 },
    { tempId: '4.2', code: '4.2', name: 'Ingresos Extraordinarios', category: 'INGRESO', parentTempId: '4', isSelectable: true, level: 2, initialBalance: 0 },

    // 5 - EGRESO
    { tempId: '5', code: '5', name: 'EGRESO', category: 'EGRESO', parentTempId: null, isSelectable: false, level: 1, initialBalance: 0 },
    { tempId: '5.1', code: '5.1', name: 'Gastos Ordinarios', category: 'EGRESO', parentTempId: '5', isSelectable: true, level: 2, initialBalance: 0 },
    { tempId: '5.2', code: '5.2', name: 'Gastos Extraordinarios', category: 'EGRESO', parentTempId: '5', isSelectable: true, level: 2, initialBalance: 0 }
  ];

  // 1. DYNAMIC: Finance Accounts (Cash/Banks & Credit Cards)
  const cashAndBanks = financeAccounts.filter(a => a.type !== 'TARJETA DE CREDITO');
  const creditCards = financeAccounts.filter(a => a.type === 'TARJETA DE CREDITO');

  cashAndBanks.forEach((acc, idx) => {
    const inst = getInstitutionById(acc.institutionId || '');
    const bankPrefix = inst && inst.id !== 'otros' && inst.id !== 'efectivo' ? `${inst.name} - ` : '';
    const code = `1.1.01.${(idx + 1).toString().padStart(2, '0')}`;
    template.push({
      tempId: `finance_${acc.id}`,
      code,
      name: `${bankPrefix}${acc.name}`,
      category: 'ACTIVO',
      parentTempId: '1.1.01',
      isSelectable: true,
      level: 4,
      initialBalance: 0,
      linkedFinanceAccountId: acc.id
    });
  });

  creditCards.forEach((acc, idx) => {
    const inst = getInstitutionById(acc.institutionId || '');
    const bankPrefix = inst ? `${inst.name} - ` : '';
    const code = `1.1.02.${(idx + 1).toString().padStart(2, '0')}`;
    template.push({
      tempId: `finance_${acc.id}`,
      code,
      name: `${bankPrefix}${acc.name}`,
      category: 'ACTIVO',
      parentTempId: '1.1.02',
      isSelectable: true,
      level: 4,
      initialBalance: 0,
      linkedFinanceAccountId: acc.id
    });
  });

  // Map to store generated document IDs
  const idMap: Record<string, string> = {};

  // First pass: generate IDs
  template.forEach(item => {
    idMap[item.tempId] = newId();
  });

  // Second pass: insert with correct parentIds
  // nota: sin batch atómico; orden pensado para fallo parcial seguro
  // (el template lista siempre padres antes que hijos, y las cuentas dinámicas
  // van al final: nunca se inserta un hijo cuyo padre no exista todavía).
  for (const item of template) {
    const docId = idMap[item.tempId];
    const parentId = item.parentTempId ? idMap[item.parentTempId] : null;

    const { tempId, parentTempId, ...accData } = item;

    await insertRow('accountingAccounts', {
      id: docId,
      ...accData,
      parentId,
      userId,
      createdAt: timestamp
    });
  }
};

export const generateJournalEntriesFromDocuments = async (
  userId: string,
  accounts: AccountingAccount[],
  financeAccounts: Account[]
) => {
  const allDocs = await listUserRows<ManagedDocument>('managedDocuments', userId);

  const docsToProcess = allDocs.filter(d => !d.journalEntryId);

  if (docsToProcess.length === 0) {
    throw new Error('No hay nuevos documentos pendientes de asentar.');
  }

  // Fetch current articles to get latest accounting accounts
  const articles = await listUserRows<any>('managedArticles', userId);

  // Find base accounts
  const accountsPayable = accounts.find(a => a.code === '2.1.01'); // Cuentas a Pagar Comerciales
  const ordinaryExpenses = accounts.find(a => a.code === '5.1'); // Gastos Ordinarios
  const ordinaryIncome = accounts.find(a => a.code === '4.1'); // Ingresos Ordinarios
  const accountsReceivable = accounts.find(a => a.code === '1.1.03'); // Cuentas a Cobrar

  if (!accountsPayable || !ordinaryExpenses || !ordinaryIncome || !accountsReceivable) {
    throw new Error('No se encontraron las cuentas contables base (2.1.01, 5.1, 4.1, 1.1.03). Asegúrate de inicializar el módulo.');
  }

  // We should theoretically calculate transaction balances, but for bulk generate
  // we might not have them easily. Let's do a simple one or we can fetch all transactions.
  const transactions = await listUserRows<Transaction>('transactions', userId);

  const balances = calculateTransactionBalances(transactions, allDocs);
  let successCount = 0;
  const errors: string[] = [];

  for (const managedDoc of docsToProcess) {
    const entryId = newId();
    const lines: any[] = [];
    // Safely fallback required fields for older documents
    const safeDate = managedDoc.date || new Date().toISOString().split('T')[0];
    const safeType = managedDoc.type || 'PURCHASE_INVOICE';
    const safeContactName = managedDoc.contactName || 'S/N';
    const safeInternalContactName = managedDoc.internalContactName || 'S/N';
    const safeTotalAmount = managedDoc.totalAmount === undefined || managedDoc.totalAmount === null ? 0 : managedDoc.totalAmount;
    const safeCreatedAt = managedDoc.createdAt || new Date().toISOString();
    const safeCondition = managedDoc.condition || 'CONTADO';
    const safeItems = managedDoc.items || [];

    const description = `${safeType === 'PURCHASE_INVOICE' ? 'Factura Compra' :
                         safeType === 'SALES_INVOICE' ? 'Factura Venta' :
                         safeType === 'INTERNAL_EXPENSE' ? 'Egreso Interno' : 'Ingreso Interno'} - ${safeContactName} - ${managedDoc.number || 'SN'}`;

    const isExpense = safeType === 'PURCHASE_INVOICE' || safeType === 'INTERNAL_EXPENSE';

    // 1. Lines for Items (Detailed)
    if (safeItems.length === 0) {
      throw new Error(`El documento ${managedDoc.number || 'SN'} no tiene items. Asegúrate de actualizar el documento.`);
    }

    safeItems.forEach(item => {
      let itemAccId = item.accountingAccountId || '';

      // Override from catalog logic
      if (item.articleId) {
        const catArticle = articles.find(a => a.id === item.articleId);
        if (catArticle && catArticle.accountingAccountId) {
          itemAccId = catArticle.accountingAccountId;
        }
      }

      if (!itemAccId) itemAccId = isExpense ? ordinaryExpenses.id : ordinaryIncome.id;

      lines.push({
        accountId: itemAccId,
        debit: isExpense ? item.total : 0,
        credit: isExpense ? 0 : item.total,
        description: safeItems.length > 1 ? `Detalle: ${item.total}` : ''
      });
    });

    // 2. Offset Lines
    let hasAllocatedTransactions = false;
    let allocatedAmount = 0;

    if (safeCondition === 'CONTADO' && managedDoc.transactionIds && managedDoc.transactionIds.length > 0) {
      for (const txId of managedDoc.transactionIds) {
        const tx = transactions.find(t => t.id === txId);
        const tInfo = balances[txId];
        const allocationAmount = tInfo?.allocations.find((a: any) => a.docId === managedDoc.id)?.amount || 0;

        if (tx && allocationAmount > 0.01) {
          const primaryAccountId = tx.details[0]?.accountId;
          const linkedAcc = accounts.find(a => a.linkedFinanceAccountId === primaryAccountId);
          const accId = linkedAcc ? linkedAcc.id : '';

          if (accId) {
            lines.push({
              accountId: accId,
              debit: isExpense ? 0 : allocationAmount,
              credit: isExpense ? allocationAmount : 0,
              description: `Cobro/Pago mov #${txId.substring(0, 4)}`
            });
            allocatedAmount += allocationAmount;
            hasAllocatedTransactions = true;
          }
        }
      }
    }

    if (hasAllocatedTransactions) {
      const diff = safeTotalAmount - allocatedAmount;
      if (Math.abs(diff) > 0.01) {
        const defaultAccId = isExpense ? accountsPayable.id : accountsReceivable.id;
        lines.push({
          accountId: defaultAccId,
          debit: isExpense ? 0 : diff,
          credit: isExpense ? diff : 0
        });
      }
    } else {
      let offsetAccId = '';
      if (safeCondition === 'CONTADO') {
        const cashAcc = accounts.find(a => a.code.startsWith('1.1.01') && a.isSelectable);
        if (cashAcc) offsetAccId = cashAcc.id;
      }
      if (!offsetAccId) {
        offsetAccId = isExpense ? accountsPayable.id : accountsReceivable.id;
      }
      lines.push({
        accountId: offsetAccId,
        debit: isExpense ? 0 : safeTotalAmount,
        credit: isExpense ? safeTotalAmount : 0
      });
    }

    const finalLines = groupJournalLines(lines);

    if (finalLines.length < 2) {
      throw new Error(`Asiento inválido para el documento ${managedDoc.number || 'SN'}: Debe contener al menos 2 líneas. (Cuentas detectadas: ${finalLines.map(l => l.accountId).join(', ')})`);
    }

    const journalEntry = {
      id: entryId,
      date: safeDate,
      description,
      type: 'OPERATIVO',
      lines: finalLines,
      userId,
      documentLocatorCode: managedDoc.locatorCode || null,
      createdAt: new Date().toISOString()
    };

    try {
      // Ensure grandfathered documents pass security rules
      const finalDoc: any = {};
      finalDoc.id = managedDoc.id;
      finalDoc.userId = userId;
      finalDoc.journalEntryId = entryId;

      const allowedVars = ['id', 'type', 'date', 'number', 'timbrado', 'cdc', 'internalContactId', 'internalContactName', 'contactId', 'contactName', 'condition', 'totalAmount', 'currency', 'exchangeRate', 'foreignTotalAmount', 'tax5Amount', 'tax10Amount', 'baseDocumentUrl', 'locatorCode', 'observations', 'userId', 'transactionIds', 'isCancelled', 'remainingAmount', 'journalEntryId', 'items', 'isHistorical', 'historicalPendingAmount', 'createdAt', 'parentDocumentId', 'applications', 'appliedDocumentIds'];

      Object.keys(managedDoc).forEach(key => {
        const val = (managedDoc as any)[key];
        if (allowedVars.includes(key) && val !== undefined && val !== null) {
          finalDoc[key] = val;
        }
      });

      // ENFORCE REQUIRED TYPES
      finalDoc.internalContactName = finalDoc.internalContactName ? String(finalDoc.internalContactName) : safeInternalContactName;
      finalDoc.contactName = finalDoc.contactName ? String(finalDoc.contactName) : safeContactName;
      finalDoc.date = finalDoc.date ? String(finalDoc.date) : safeDate;
      finalDoc.totalAmount = finalDoc.totalAmount !== undefined ? Number(finalDoc.totalAmount) : Number(safeTotalAmount);
      finalDoc.createdAt = (!finalDoc.createdAt || typeof finalDoc.createdAt !== 'string') ? new Date().toISOString() : String(finalDoc.createdAt);
      finalDoc.condition = finalDoc.condition ? String(finalDoc.condition) : safeCondition;
      finalDoc.items = Array.isArray(finalDoc.items) ? finalDoc.items : safeItems;
      finalDoc.type = finalDoc.type ? String(finalDoc.type) : safeType;
      finalDoc.userId = String(userId);

      // ENFORCE OPTIONAL NUMBERS
      if ('tax5Amount' in finalDoc) finalDoc.tax5Amount = Number(finalDoc.tax5Amount);
      if ('tax10Amount' in finalDoc) finalDoc.tax10Amount = Number(finalDoc.tax10Amount);
      if ('exchangeRate' in finalDoc) finalDoc.exchangeRate = Number(finalDoc.exchangeRate);
      if ('foreignTotalAmount' in finalDoc) finalDoc.foreignTotalAmount = Number(finalDoc.foreignTotalAmount);
      if ('remainingAmount' in finalDoc) finalDoc.remainingAmount = Number(finalDoc.remainingAmount);
      if ('historicalPendingAmount' in finalDoc) finalDoc.historicalPendingAmount = Number(finalDoc.historicalPendingAmount);

      // ENFORCE OPTIONAL STRINGS & BOUNDARIES
      if ('number' in finalDoc) finalDoc.number = String(finalDoc.number);
      if ('timbrado' in finalDoc) finalDoc.timbrado = String(finalDoc.timbrado).substring(0, 8);
      if ('cdc' in finalDoc) finalDoc.cdc = String(finalDoc.cdc).substring(0, 44);
      if ('locatorCode' in finalDoc) finalDoc.locatorCode = String(finalDoc.locatorCode).substring(0, 5);
      if ('observations' in finalDoc) finalDoc.observations = String(finalDoc.observations);
      if ('baseDocumentUrl' in finalDoc) finalDoc.baseDocumentUrl = String(finalDoc.baseDocumentUrl);
      if ('contactId' in finalDoc) finalDoc.contactId = String(finalDoc.contactId);
      if ('internalContactId' in finalDoc) finalDoc.internalContactId = String(finalDoc.internalContactId);
      if ('parentDocumentId' in finalDoc) finalDoc.parentDocumentId = String(finalDoc.parentDocumentId);
      if ('currency' in finalDoc && !['PYG', 'USD', 'ARS', 'BRL'].includes(finalDoc.currency)) delete finalDoc.currency;

      // ENFORCE BOOLEANS
      if ('isCancelled' in finalDoc) finalDoc.isCancelled = Boolean(finalDoc.isCancelled);
      else finalDoc.isCancelled = false; // ensure isCancelled is defined for all records as an explicit fallback if needed, wait, rule says optional. But it's fine.

      if ('isHistorical' in finalDoc) finalDoc.isHistorical = Boolean(finalDoc.isHistorical);

      // ENFORCE LISTS
      if ('transactionIds' in finalDoc && !Array.isArray(finalDoc.transactionIds)) delete finalDoc.transactionIds;
      if ('applications' in finalDoc && !Array.isArray(finalDoc.applications)) delete finalDoc.applications;
      if ('appliedDocumentIds' in finalDoc && !Array.isArray(finalDoc.appliedDocumentIds)) delete finalDoc.appliedDocumentIds;


      console.log('Writing Journal Entry:', JSON.stringify(journalEntry, null, 2));
      console.log('Writing Document Update:', JSON.stringify(finalDoc, null, 2));

      // nota: sin batch atómico; orden pensado para fallo parcial seguro
      // (primero el asiento, después el vínculo en el documento; si el segundo
      // paso falla, el documento sigue "pendiente" y el error queda registrado).
      const { id: docId, ...docValues } = finalDoc;
      await insertRow('accountingEntries', journalEntry);
      await updateRow('managedDocuments', managedDoc.id, docValues);
      successCount++;
    } catch (err: any) {
      console.error(`Error procesando documento ${managedDoc.number || managedDoc.id}:`, err);
      errors.push(`Doc ${managedDoc.number || 'SN'}: ${err.message}`);
    }
  }

  if (errors.length > 0) {
    if (successCount === 0) {
      throw new Error(`Fallo total. No se pudo procesar ningún documento. Errores: ${errors.join(' | ')}`);
    }
    throw new Error(`Proceso completado con ${errors.length} errores. Asentados: ${successCount}. Errores: ${errors.join(' | ')}`);
  }
};
